import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Ruler, Target, Activity, TrendingUp, LogOut,
  AlertTriangle, CheckCircle2, MinusCircle, XCircle, HelpCircle,
} from "lucide-react";

// The chart.
//
// Everything on this page that makes a CLAIM about the patient - a goal met, a
// measure improving, a discharge outcome - is computed by the server from the
// measurements. Nothing here lets a user type a status, because a typed status
// is an opinion and this one has to be evidence.

const STATUS_STYLE = {
  met:             { icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Met" },
  improving:       { icon: TrendingUp,   cls: "text-teal-700 bg-teal-50 border-teal-200",         label: "Improving" },
  no_change:       { icon: MinusCircle,  cls: "text-amber-700 bg-amber-50 border-amber-200",      label: "No change" },
  regressed:       { icon: XCircle,      cls: "text-rose-700 bg-rose-50 border-rose-200",         label: "Regressed" },
  no_progress_yet: { icon: HelpCircle,   cls: "text-slate-600 bg-slate-50 border-slate-200",      label: "Not re-measured" },
  not_measured:    { icon: HelpCircle,   cls: "text-slate-500 bg-slate-50 border-slate-200",      label: "Never measured" },
};

function GoalStatus({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.not_measured;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${s.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {s.label}
    </span>
  );
}

function ProgressBar({ pct }) {
  const v = Math.max(0, Math.min(100, pct ?? 0));
  return (
    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${v}%` }} />
    </div>
  );
}

const KIND = {
  rom: "ROM", mmt: "Strength", pain_nprs: "Pain",
  girth: "Girth", leg_length: "Leg length", special_test: "Test",
};

// The measure type has to be IN the label. Without it, a knee flexion range and
// a knee flexion muscle grade render as two identical rows differing only by
// their unit, and a special test renders as a bare region with no test name.
function label(m) {
  const kind = KIND[m.measure_type] || m.measure_type;
  const side = m.side && m.side !== "na" ? ` (${m.side})` : "";
  if (m.measure_type === "special_test") {
    return `${m.test_name || "Special test"}${side}`;
  }
  const where = [m.region, m.movement?.replace(/_/g, " ")].filter(Boolean).join(" ");
  return `${kind} · ${where}${side}`;
}

const UNIT = { rom: "°", mmt: "/5", pain_nprs: "/10", girth: " cm", leg_length: " cm" };

export default function PhysioPatientChart() {
  const { id, planId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const roles = useMemo(
    () => (Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.role]).filter(Boolean),
    [user],
  );
  const isTherapist = roles.some((r) => ["doctor", "admin", "hospital_admin"].includes(r));

  const [plan, setPlan] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reassess, setReassess] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("goals");

  const [measureOpen, setMeasureOpen] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [dischargeOpen, setDischargeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let pid = planId;
      if (!pid) {
        const { data } = await api.get(`/physio/plans?patient_id=${id}`);
        const plans = data.plans || [];
        const active = plans.find((p) => p.status === "active") || plans[0];
        if (!active) { setPlan(null); setLoading(false); return; }
        pid = active.id;
      }
      const { data: d } = await api.get(`/physio/plans/${pid}`);
      setDetail(d);
      setPlan(d.plan);
      const [{ data: ra }, { data: mt }] = await Promise.all([
        api.get(`/physio/patients/${d.plan.patient_id}/reassessment`),
        meta ? Promise.resolve({ data: meta }) : api.get("/physio/meta"),
      ]);
      setReassess(ra);
      setMeta(mt);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id, planId, meta]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, planId]);

  if (loading) return <div className="p-8 text-slate-500">Loading the chart…</div>;
  if (!plan) {
    return (
      <div className="p-8 space-y-4">
        <Button variant="ghost" onClick={() => nav(-1)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <Card className="p-6">
          <div className="font-semibold text-slate-800">No plan of care yet</div>
          <p className="text-sm text-slate-500 mt-1">
            This patient has been referred but not evaluated. Record the initial
            evaluation, then create the plan of care.
          </p>
          {isTherapist && (
            <Button className="mt-4" onClick={() => setMeasureOpen(true)}>
              <Ruler className="h-4 w-4 mr-1.5" />Start initial evaluation
            </Button>
          )}
        </Card>
        <MeasureDialog open={measureOpen} onOpenChange={setMeasureOpen} meta={meta}
                       patientId={id} isBaseline onDone={load} />
      </div>
    );
  }

  const pr = detail.progress || {};
  const gs = detail.goals_summary || {};
  const bal = detail.balance;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="physio-chart">
      <Button variant="ghost" size="sm" onClick={() => nav("/physio")}>
        <ArrowLeft className="h-4 w-4 mr-1" />Board
      </Button>

      {/* Header: who, what, and where they are in the course of treatment */}
      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-extrabold text-slate-900">{detail.patient?.full_name}</h1>
            <div className="text-sm text-slate-500">
              {detail.patient?.age ? `${detail.patient.age}y` : ""} {detail.patient?.sex || ""}
            </div>
            <div className="mt-2 font-semibold text-slate-800">{plan.pt_diagnosis}</div>
            <div className="text-xs text-slate-500">
              {plan.frequency_per_week}×/week for {plan.duration_weeks} weeks
              {" · "}therapist {plan.therapist_name}
              {plan.precautions ? <> · <span className="text-amber-700 font-medium">{plan.precautions}</span></> : null}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge variant={plan.status === "active" ? "default" : "secondary"}>{plan.status}</Badge>
            {pr.behind_schedule && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                <AlertTriangle className="h-3.5 w-3.5" /> Behind schedule
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs text-slate-500">Sessions</div>
            <div className="font-bold tabular-nums">{pr.completed} / {pr.authorized_sessions}</div>
            <ProgressBar pct={pr.authorized_sessions ? (pr.completed / pr.authorized_sessions) * 100 : 0} />
          </div>
          <div>
            <div className="text-xs text-slate-500">Attendance</div>
            <div className="font-bold tabular-nums">
              {pr.attendance_rate_pct != null ? `${pr.attendance_rate_pct}%` : "—"}
            </div>
            <div className="text-xs text-slate-400">{pr.no_show} no-show · {pr.cancelled} cancelled</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Goals met</div>
            <div className="font-bold tabular-nums">{gs.met} / {gs.total}</div>
            {gs.stalled ? <div className="text-xs text-rose-600 font-medium">{gs.stalled} stalled</div> : null}
          </div>
          <div>
            <div className="text-xs text-slate-500">Sessions funded</div>
            {bal ? (
              <>
                <div className="font-bold tabular-nums">{bal.remaining} left</div>
                <div className="text-xs text-slate-400">
                  {bal.funding_source === "hmo" ? `${bal.hmo_name} · ${bal.loa_number}` : "Self-paid"}
                  {bal.days_to_expiry != null ? ` · ${bal.days_to_expiry}d` : ""}
                </div>
              </>
            ) : (
              <div className="text-sm font-semibold text-rose-700">None on file</div>
            )}
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        {isTherapist && plan.status === "active" && (
          <>
            <Button onClick={() => setMeasureOpen(true)}>
              <Ruler className="h-4 w-4 mr-1.5" />Re-measure
            </Button>
            <Button variant="outline" onClick={() => setDischargeOpen(true)}>
              <LogOut className="h-4 w-4 mr-1.5" />Discharge
            </Button>
          </>
        )}
        {plan.status === "active" && (
          <Button variant="outline" onClick={() => setSessionOpen(true)}>
            <Activity className="h-4 w-4 mr-1.5" />Record session
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="goals"><Target className="h-4 w-4 mr-1.5" />Goals</TabsTrigger>
          <TabsTrigger value="progress"><TrendingUp className="h-4 w-4 mr-1.5" />Progress</TabsTrigger>
          <TabsTrigger value="sessions"><Activity className="h-4 w-4 mr-1.5" />Sessions</TabsTrigger>
        </TabsList>

        {/* ------------------------------------------------------------ goals */}
        <TabsContent value="goals" className="space-y-3 pt-3">
          {(detail.goals || []).length === 0 && (
            <Card className="p-6 text-sm text-slate-500">
              No goals on this plan yet.
            </Card>
          )}
          {(detail.goals || []).map((g) => (
            <Card key={g.goal_id} className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{g.description}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {g.term === "long" ? "Long-term" : "Short-term"} · target by {g.target_date}
                    {g.overdue && g.status !== "met" && (
                      <span className="ml-2 text-rose-700 font-semibold">overdue</span>
                    )}
                  </div>
                </div>
                <GoalStatus status={g.status} />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-500">Baseline</div>
                  <div className="font-bold tabular-nums">{g.baseline_value ?? "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Latest</div>
                  <div className="font-bold tabular-nums text-teal-700">{g.latest_value ?? "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Target</div>
                  <div className="font-bold tabular-nums">{g.target_value}</div>
                </div>
              </div>
              {g.progress_pct != null && (
                <div className="mt-2"><ProgressBar pct={g.progress_pct} /></div>
              )}
            </Card>
          ))}
        </TabsContent>

        {/* --------------------------------------------------------- progress */}
        <TabsContent value="progress" className="space-y-4 pt-3">
          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b">
              <div className="font-bold text-slate-900">Objective measures</div>
              <div className="text-xs text-slate-500">Baseline against the latest reading of the same series.</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left font-semibold px-4 py-2">Measure</th>
                    <th className="text-right font-semibold px-4 py-2">Baseline</th>
                    <th className="text-right font-semibold px-4 py-2">Latest</th>
                    <th className="text-right font-semibold px-4 py-2">Change</th>
                    <th className="text-left font-semibold px-4 py-2">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {(reassess?.measures || []).map((m, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-4 py-2 capitalize">{label(m)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {m.baseline ?? m.baseline_result ?? "—"}{m.baseline != null ? (UNIT[m.measure_type] || "") : ""}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">
                        {m.latest ?? m.latest_result ?? "—"}{m.latest != null ? (UNIT[m.measure_type] || "") : ""}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {m.delta != null ? (m.delta > 0 ? `+${m.delta}` : m.delta) : "—"}
                      </td>
                      <td className="px-4 py-2">
                        {m.trend === "improved" && <span className="text-emerald-700 font-semibold">improved</span>}
                        {m.trend === "worsened" && <span className="text-rose-700 font-semibold">worsened</span>}
                        {m.trend === "unchanged" && <span className="text-slate-500">unchanged</span>}
                        {m.trend == null && (m.changed ? <span className="text-emerald-700">now {m.latest_result}</span>
                                                       : <span className="text-slate-500">{m.latest_result}</span>)}
                      </td>
                    </tr>
                  ))}
                  {(reassess?.measures || []).length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-slate-500">
                      Only a baseline exists. Re-measure to produce a trend.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {(reassess?.instruments || []).map((i) => (
            <Card key={i.instrument} className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-bold text-slate-900">{i.label}</div>
                  <div className="text-xs text-slate-500">
                    {i.baseline} → <span className="font-semibold text-slate-700">{i.latest}</span>
                    {" · "}change {i.improvement > 0 ? `+${i.improvement}` : i.improvement}
                    {" · "}{i.basis === "mcid" ? "MCID" : "MDC"} {i.threshold}
                  </div>
                </div>
                <div className="text-right">
                  {/* Berg and TUG have NO published MCID, so their verdict is
                      against measurement error instead. Rendering both bases the
                      same way would let the weaker claim read as the stronger one. */}
                  {i.verdict === "improved_beyond_mcid" && (
                    <Badge className="bg-emerald-600">Clinically meaningful improvement</Badge>
                  )}
                  {i.verdict === "within_mcid" && (
                    <Badge variant="secondary">Below the clinical threshold</Badge>
                  )}
                  {i.verdict === "worsened_beyond_mcid" && (
                    <Badge className="bg-rose-600">Meaningful deterioration</Badge>
                  )}
                  {i.verdict === "improved_beyond_mdc" && (
                    <Badge className="bg-teal-600">Improved beyond measurement error</Badge>
                  )}
                  {i.verdict === "within_measurement_error" && (
                    <Badge variant="secondary">Within measurement error</Badge>
                  )}
                  {i.verdict === "worsened_beyond_mdc" && (
                    <Badge className="bg-rose-600">Worsened beyond measurement error</Badge>
                  )}
                  {i.verdict === "cannot_assess" && (
                    <Badge variant="outline">No threshold published</Badge>
                  )}
                  {i.basis === "mdc" && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      no published MCID — judged against MDC
                    </div>
                  )}
                </div>
              </div>
              {/* Provenance travels WITH the verdict. A verdict quoted downstream
                  without it becomes a fact nobody checked. An MCID also belongs
                  to a POPULATION, so the population is part of the provenance. */}
              <div className="mt-3 text-xs text-slate-600 bg-slate-50 border rounded-md px-3 py-2 space-y-1">
                <div>
                  <span className="font-semibold">
                    {i.basis === "mcid" ? "MCID" : "MDC"} {i.threshold}
                  </span>
                  {i.population ? <> · derived on {i.population}</> : null}
                  {i.verified
                    ? <> · <span className="text-emerald-700 font-medium">verified {i.verified_on}</span></>
                    : <> · <span className="text-amber-700 font-medium">unverified — a clinician should confirm this</span></>}
                </div>
                {i.source ? <div className="italic">{i.source}</div> : null}
                {i.note ? <div className="text-slate-500">{i.note}</div> : null}
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* --------------------------------------------------------- sessions */}
        <TabsContent value="sessions" className="pt-3">
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left font-semibold px-4 py-2">Date</th>
                    <th className="text-left font-semibold px-4 py-2">Attendance</th>
                    <th className="text-left font-semibold px-4 py-2">Pain</th>
                    <th className="text-left font-semibold px-4 py-2">Interventions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(detail.sessions || [])].reverse().map((s) => (
                    <tr key={s.id} className="border-t align-top">
                      <td className="px-4 py-2 whitespace-nowrap">{(s.session_date || "").slice(0, 10)}</td>
                      <td className="px-4 py-2">
                        {s.attendance === "completed" && <span className="text-emerald-700 font-medium">completed</span>}
                        {s.attendance === "no_show" && <span className="text-rose-700 font-medium">no-show</span>}
                        {s.attendance === "cancelled" && <span className="text-slate-500">cancelled</span>}
                        {s.balance_overridden && (
                          <div className="text-xs text-amber-800 mt-0.5">
                            recorded past the authorization — {s.override_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 tabular-nums whitespace-nowrap">
                        {s.pain_before != null ? `${s.pain_before} → ${s.pain_after ?? "?"}` : "—"}
                      </td>
                      <td className="px-4 py-2 text-slate-600">
                        {[
                          ...(s.exercises || []).map((e) =>
                            `${e.name}${e.sets ? ` ${e.sets}×${e.reps || ""}` : ""}`),
                          ...(s.manual_therapy || []).map((m) => m.technique),
                          ...(s.modalities || []).map((m) => `${m.modality} ${m.minutes}min`),
                        ].join(" · ") || <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  ))}
                  {(detail.sessions || []).length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-slate-500">No sessions recorded yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {detail.session_count > (detail.sessions || []).length && (
              <div className="px-4 py-2 text-xs text-slate-500 border-t">
                Showing the most recent {(detail.sessions || []).length} of {detail.session_count}.
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <MeasureDialog open={measureOpen} onOpenChange={setMeasureOpen} meta={meta}
                     patientId={plan.patient_id} onDone={load} />
      <SessionDialog open={sessionOpen} onOpenChange={setSessionOpen} meta={meta}
                     plan={plan} onDone={load} />
      <DischargeDialog open={dischargeOpen} onOpenChange={setDischargeOpen}
                       plan={plan} onDone={load} />
    </div>
  );
}

/* ------------------------------------------------------------------ dialogs */

function MeasureDialog({ open, onOpenChange, meta, patientId, isBaseline = false, onDone }) {
  const [region, setRegion] = useState("knee");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const movements = meta?.rom_movements?.[region] || [];
  const bilateral = (meta?.bilateral_regions || []).includes(region);

  useEffect(() => {
    if (!open) return;
    // Pre-load the region's movements so a therapist fills numbers rather than
    // choosing what to measure - the picker is built from the SERVER's list, so
    // the UI cannot offer a movement the API will refuse.
    setRows(movements.map((m) => ({
      measure_type: "rom", region, movement: m.movement,
      side: bilateral ? "right" : "na", value: "",
      normal: m.normal_degrees,
    })).concat([
      { measure_type: "pain_nprs", region, movement: "", side: bilateral ? "right" : "na", value: "" },
    ]));
    /* eslint-disable-next-line */
  }, [open, region, meta]);

  const submit = async () => {
    const measurements = rows
      .filter((r) => r.value !== "" && r.value != null)
      .map((r) => ({
        measure_type: r.measure_type, region: r.region,
        movement: r.movement || "", side: r.side,
        value: Number(r.value),
      }));
    if (!measurements.length) { toast.error("Enter at least one reading."); return; }
    setSaving(true);
    try {
      await api.post(`/physio/patients/${patientId}/measurements`,
                     { measurements, is_baseline: isBaseline });
      toast.success(`${measurements.length} measurement(s) recorded`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isBaseline ? "Initial evaluation" : "Re-measure"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            {(meta?.regions || []).map((r) => (
              <Button key={r} size="sm" variant={r === region ? "default" : "outline"}
                      onClick={() => setRegion(r)} className="capitalize">
                {r}
              </Button>
            ))}
          </div>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex-1 text-sm capitalize">
                  {r.measure_type === "pain_nprs"
                    ? "Pain (0–10)"
                    : (r.movement || "").replace(/_/g, " ")}
                  {r.normal ? <span className="text-xs text-slate-400"> · normal {r.normal}°</span> : null}
                </div>
                {bilateral && (
                  <select
                    className="border rounded-md text-sm px-2 py-1"
                    value={r.side}
                    onChange={(e) => setRows((x) =>
                      x.map((row, j) => j === i ? { ...row, side: e.target.value } : row))}
                  >
                    <option value="right">right</option>
                    <option value="left">left</option>
                  </select>
                )}
                <Input
                  type="number" className="w-28" placeholder="—"
                  value={r.value}
                  onChange={(e) => setRows((x) =>
                    x.map((row, j) => j === i ? { ...row, value: e.target.value } : row))}
                />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SessionDialog({ open, onOpenChange, meta, plan, onDone }) {
  const [painBefore, setPainBefore] = useState("");
  const [painAfter, setPainAfter] = useState("");
  const [exercises, setExercises] = useState([{ name: "", sets: 3, reps: 10 }]);
  const [modality, setModality] = useState("");
  const [minutes, setMinutes] = useState(15);
  const [subjective, setSubjective] = useState("");
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [reason, setReason] = useState("");

  const post = async (override) => {
    const body = {
      plan_id: plan.id,
      attendance: "completed",
      pain_before: painBefore === "" ? null : Number(painBefore),
      pain_after: painAfter === "" ? null : Number(painAfter),
      exercises: exercises.filter((e) => e.name.trim())
        .map((e) => ({ name: e.name, sets: Number(e.sets) || null, reps: Number(e.reps) || null })),
      modalities: modality ? [{ modality, minutes: Number(minutes) || 15 }] : [],
      manual_therapy: [],
      subjective,
    };
    let url = `/physio/plans/${plan.id}/sessions`;
    if (override) {
      url += `?override=true&override_reason=${encodeURIComponent(reason)}`;
    }
    setSaving(true);
    try {
      await api.post(url, body);
      toast.success("Session recorded");
      setBlocked(null); setReason("");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = formatErr(e?.response?.data?.detail);
      // The 409 is not an error to bury in a toast - it is a decision the
      // person in front of the patient has to make, so it is put to them.
      if (status === 409) setBlocked(detail);
      else toast.error(detail);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setBlocked(null); setReason(""); } }}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Record a treatment session</DialogTitle></DialogHeader>

        {blocked ? (
          <div className="space-y-3">
            <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              <div className="font-semibold flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" /> This session is not funded
              </div>
              <div className="mt-1">{blocked}</div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">
                Reason for treating anyway <span className="text-rose-600">*</span>
              </div>
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. HMO renewal in process, approved by the clinic director" />
              <p className="text-xs text-slate-500 mt-1">
                This is recorded against the session and written to the audit log.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setBlocked(null)}>Back</Button>
              <Button variant="destructive" disabled={!reason.trim() || saving}
                      onClick={() => post(true)}>
                Record anyway
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-sm font-medium mb-1">Pain before (0–10)</div>
                  <Input type="number" value={painBefore} onChange={(e) => setPainBefore(e.target.value)} />
                </div>
                <div>
                  <div className="text-sm font-medium mb-1">Pain after (0–10)</div>
                  <Input type="number" value={painAfter} onChange={(e) => setPainAfter(e.target.value)} />
                </div>
              </div>

              <div>
                <div className="text-sm font-medium mb-1">Therapeutic exercise</div>
                {exercises.map((e, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <Input className="flex-1" placeholder="Exercise" value={e.name}
                           onChange={(ev) => setExercises((x) =>
                             x.map((r, j) => j === i ? { ...r, name: ev.target.value } : r))} />
                    <Input className="w-20" type="number" placeholder="sets" value={e.sets}
                           onChange={(ev) => setExercises((x) =>
                             x.map((r, j) => j === i ? { ...r, sets: ev.target.value } : r))} />
                    <Input className="w-20" type="number" placeholder="reps" value={e.reps}
                           onChange={(ev) => setExercises((x) =>
                             x.map((r, j) => j === i ? { ...r, reps: ev.target.value } : r))} />
                  </div>
                ))}
                <Button size="sm" variant="outline"
                        onClick={() => setExercises((x) => [...x, { name: "", sets: 3, reps: 10 }])}>
                  <Plus className="h-3.5 w-3.5 mr-1" />Add exercise
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <div className="text-sm font-medium mb-1">Modality</div>
                  <select className="w-full border rounded-md text-sm px-2 py-2"
                          value={modality} onChange={(e) => setModality(e.target.value)}>
                    <option value="">— none —</option>
                    {(meta?.modalities || []).map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-sm font-medium mb-1">Minutes</div>
                  <Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
                </div>
              </div>

              <div>
                <div className="text-sm font-medium mb-1">Subjective</div>
                <Textarea rows={2} value={subjective} onChange={(e) => setSubjective(e.target.value)}
                          placeholder="What the patient reports today" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={() => post(false)} disabled={saving}>
                {saving ? "Saving…" : "Record session"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DischargeDialog({ open, onOpenChange, plan, onDone }) {
  const [preview, setPreview] = useState(null);
  const [outcome, setOutcome] = useState("");
  const [summary, setSummary] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/physio/plans/${plan.id}/discharge-preview`)
      .then(({ data }) => {
        setPreview(data);
        setOutcome(data.suggested_outcome);
        const p = data.progress || {};
        setSummary(
          `Completed ${p.completed} of ${p.authorized_sessions} authorized sessions. ` +
          `${data.goals_summary?.met ?? 0} of ${data.goals_summary?.total ?? 0} goals met.`,
        );
      })
      .catch((e) => toast.error(formatErr(e?.response?.data?.detail)));
  }, [open, plan.id]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/physio/plans/${plan.id}/discharge`,
                     { outcome, summary, recommendation, notify_referrer: true });
      toast.success("Discharged");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const OUTCOMES = [
    ["goals_met", "Goals met"],
    ["partially_met", "Partially met"],
    ["not_met", "Not met"],
    ["lost_to_follow_up", "Lost to follow-up"],
    ["referred_out", "Referred out"],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Discharge</DialogTitle></DialogHeader>
        {!preview ? <div className="text-sm text-slate-500">Reading the record…</div> : (
          <div className="space-y-3">
            <div className="text-sm bg-slate-50 border rounded-md px-3 py-2">
              <span className="font-semibold">The record suggests: </span>
              <span className="font-bold text-teal-700">
                {OUTCOMES.find(([k]) => k === preview.suggested_outcome)?.[1]}
              </span>
              <div className="text-xs text-slate-500 mt-1">
                {preview.goals_summary?.met} of {preview.goals_summary?.total} goals derive to met
                from the measurements.
              </div>
            </div>

            <div>
              <div className="text-sm font-medium mb-1">Outcome</div>
              <select className="w-full border rounded-md text-sm px-2 py-2"
                      value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                {OUTCOMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              {outcome !== preview.suggested_outcome && (
                <p className="text-xs text-amber-800 mt-1">
                  This differs from what the goals show. It will be recorded as a
                  clinical override, with both values kept.
                </p>
              )}
            </div>

            <div>
              <div className="text-sm font-medium mb-1">
                Summary <span className="text-rose-600">*</span>
              </div>
              <Textarea rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} />
              <p className="text-xs text-slate-500 mt-1">
                This is the document the referring doctor receives.
              </p>
            </div>

            <div>
              <div className="text-sm font-medium mb-1">Recommendation</div>
              <Textarea rows={2} value={recommendation}
                        onChange={(e) => setRecommendation(e.target.value)} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !summary.trim()}>
            {saving ? "Saving…" : "Discharge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
